import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export type ScoreBand = "needs-work" | "developing" | "strong" | "excellent";

export function bandFor(score: number): ScoreBand {
  if (score < 50) return "needs-work";
  if (score < 75) return "developing";
  if (score < 90) return "strong";
  return "excellent";
}

const BAND_STYLES: Record<ScoreBand, { ring: string; bar: string; chip: string; label: string }> = {
  "needs-work": {
    ring: "border-primary/40 bg-primary/5",
    bar: "bg-primary",
    chip: "bg-primary/10 text-primary border-primary/30",
    label: "Needs work",
  },
  developing: {
    ring: "border-amber-400/50 bg-amber-50",
    bar: "bg-amber-500",
    chip: "bg-amber-100 text-amber-900 border-amber-300",
    label: "Developing",
  },
  strong: {
    ring: "border-violet/40 bg-violet/5",
    bar: "bg-violet",
    chip: "bg-violet/10 text-violet border-violet/30",
    label: "Strong",
  },
  excellent: {
    ring: "border-emerald-500/40 bg-emerald-50",
    bar: "bg-emerald-600",
    chip: "bg-emerald-100 text-emerald-900 border-emerald-300",
    label: "Excellent",
  },
};

export interface ResultCardProps {
  tool: "content-system-score" | "social-profile-scan" | "content-engine-advanced";
  score: number;
  levelLabel: string;
  diagnosis: string;
  gaps: string[];
  nextStep: string;
  cta: { headline: string; body: string; primaryLabel: string; secondaryLabel: string };
}

export function ResultCard({
  tool,
  score,
  levelLabel,
  diagnosis,
  gaps,
  nextStep,
  cta,
}: ResultCardProps) {
  const band = bandFor(score);
  const styles = BAND_STYLES[band];
  const [email, setEmail] = useState("");
  const [emailState, setEmailState] = useState<"idle" | "saving" | "saved">("idle");

  async function saveEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!email || emailState !== "idle") return;
    setEmailState("saving");
    try {
      await fetch("/api/public/tool-submission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool,
          score,
          level: band,
          email,
          inputSummary: `email-capture:${levelLabel}`,
        }),
      });
      setEmailState("saved");
    } catch {
      setEmailState("idle");
    }
  }

  function trackCta(target: string) {
    fetch("/api/public/tool-submission", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tool,
        score,
        level: band,
        ctaClicked: target,
        inputSummary: "cta-click",
      }),
    }).catch(() => {});
  }

  return (
    <div className="space-y-8">
      {/* Score block */}
      <div
        className={`rounded-2xl border p-8 ${styles.ring}`}
        style={{ boxShadow: "var(--shadow-soft)" }}
      >
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div
              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${styles.chip}`}
            >
              {styles.label}
            </div>
            <h2 className="font-display mt-3 text-3xl text-ink sm:text-4xl">{levelLabel}</h2>
          </div>
          <div className="flex items-baseline gap-1">
            <span className="font-display text-6xl leading-none text-ink sm:text-7xl">{score}</span>
            <span className="text-lg text-muted-foreground">/100</span>
          </div>
        </div>

        <div className="mt-6 h-2 w-full overflow-hidden rounded-full bg-ink/5">
          <div
            className={`h-full rounded-full transition-all duration-700 ${styles.bar}`}
            style={{ width: `${Math.max(4, score)}%` }}
          />
        </div>

        <p className="mt-6 text-base leading-relaxed text-ink/80">{diagnosis}</p>
      </div>

      {/* Gaps + next step */}
      <div className={`grid gap-6 ${gaps.length > 0 ? "md:grid-cols-2" : ""}`}>
        {gaps.length > 0 && (
          <div
            className="rounded-2xl border border-border bg-card p-6"
            style={{ boxShadow: "var(--shadow-soft)" }}
          >
            <h3 className="font-display text-xl text-ink">
              {gaps.length === 1 ? "What to address" : `Top ${gaps.length} things to address`}
            </h3>
            <ul className="mt-4 space-y-3">
              {gaps.map((g, i) => (
                <li key={i} className="flex gap-3 text-sm text-ink/80">
                  <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink/5 text-xs font-semibold text-ink">
                    {i + 1}
                  </span>
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div
          className="rounded-2xl border border-border bg-card p-6"
          style={{ boxShadow: "var(--shadow-soft)" }}
        >
          <h3 className="font-display text-xl text-ink">
            {gaps.length === 0 ? "Where to go from here" : "Recommended next step"}
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-ink/80">{nextStep}</p>
        </div>
      </div>

      {/* CTA card */}
      <div
        className="rounded-2xl border border-ink/15 bg-ink p-8 text-cream"
        style={{ boxShadow: "var(--shadow-soft)" }}
      >
        <h3 className="font-display text-2xl sm:text-3xl">{cta.headline}</h3>
        <p className="mt-3 max-w-2xl text-cream/80">{cta.body}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/contact"
            onClick={() => trackCta("contact")}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
          >
            {cta.primaryLabel} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/services"
            onClick={() => trackCta("services")}
            className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2.5 text-sm font-semibold text-cream transition hover:bg-cream/10"
          >
            {cta.secondaryLabel}
          </Link>
        </div>
      </div>

      {/* Unlock: advanced diagnostic for high scorers on the base content tool */}
      {tool === "content-system-score" && score >= 85 && (
        <div
          className="rounded-2xl border border-violet/30 bg-gradient-to-br from-violet/5 via-card to-primary/5 p-8"
          style={{ boxShadow: "var(--shadow-soft)" }}
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-violet/30 bg-violet/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-violet">
            Unlocked · Advanced diagnostic
          </div>
          <h3 className="font-display mt-3 text-2xl text-ink sm:text-3xl">
            You scored high. Ready for the harder one?
          </h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/80">
            The Pro Audit is 20 deeper questions covering brand guidelines, asset libraries, voice
            docs, tailored AI, knowledge bases, newsletter depth, long-form, podcasting, video, SEO,
            repurposing matrices, cross-platform analytics, attribution, and team ownership. A
            perfect score takes real depth, not just good intentions.
          </p>
          <div className="mt-6">
            <Link
              to="/tools/content-engine-advanced"
              onClick={() => trackCta("advanced-audit")}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream transition hover:opacity-90"
            >
              Take the Pro Audit <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Email capture (lead capture, not an automated send) */}
      <div
        className="rounded-2xl border border-border bg-card p-6"
        style={{ boxShadow: "var(--shadow-soft)" }}
      >
        <h3 className="font-display text-xl text-ink">Want Scott to follow up?</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Drop your email and Scott will personally reach out with notes on your score and a couple
          of next-step ideas. Optional, and no spam.
        </p>
        {emailState === "saved" ? (
          <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> Got it. Scott will be in touch.
          </div>
        ) : (
          <form onSubmit={saveEmail} className="mt-4 flex flex-col gap-3 sm:flex-row">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm text-ink placeholder:text-muted-foreground focus:border-ink/40 focus:outline-none"
            />
            <button
              type="submit"
              disabled={emailState === "saving"}
              className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream transition hover:opacity-90 disabled:opacity-50"
            >
              {emailState === "saving" ? "Sending…" : "Send to Scott"}
            </button>
          </form>
        )}
      </div>

      <p className="text-xs italic text-muted-foreground">
        This is a quick diagnostic, not a full strategy or audit. For a deeper review, contact
        Scott.
      </p>
    </div>
  );
}

export function LimitHitCard({
  tool,
}: {
  tool?: "content-system-score" | "social-profile-scan" | "content-engine-advanced";
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "saving" | "saved">("idle");

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!email || state !== "idle") return;
    setState("saving");
    try {
      await fetch("/api/public/tool-submission", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool: tool ?? "social-profile-scan",
          email,
          inputSummary: "limit-hit:email-capture",
        }),
      });
      setState("saved");
    } catch {
      setState("idle");
    }
  }

  return (
    <div
      className="rounded-2xl border border-border bg-card p-8 text-center"
      style={{ boxShadow: "var(--shadow-soft)" }}
    >
      <h2 className="font-display text-2xl text-ink sm:text-3xl">
        You've reached today's free diagnostic limit.
      </h2>
      <p className="mt-3 text-muted-foreground">
        Come back tomorrow for more free scans, or drop your email and Scott will reach out
        personally.
      </p>

      {state === "saved" ? (
        <div className="mx-auto mt-6 max-w-md rounded-xl border border-emerald-500/30 bg-emerald-50 p-4 text-sm text-emerald-900">
          <CheckCircle2 className="mx-auto mb-2 h-5 w-5" />
          Got it. Scott will be in touch.
        </div>
      ) : (
        <form
          onSubmit={submitEmail}
          className="mx-auto mt-6 flex max-w-md flex-col gap-2 sm:flex-row"
        >
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm text-ink focus:border-ink/60 focus:outline-none focus:ring-2 focus:ring-ink/10"
          />
          <button
            type="submit"
            disabled={state === "saving" || !email}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-60"
          >
            {state === "saving" ? (
              "Saving…"
            ) : (
              <>
                Email me <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      )}

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink transition hover:bg-ink/5"
        >
          Contact Scott
        </Link>
        <Link
          to="/services"
          className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm font-semibold text-ink transition hover:bg-ink/5"
        >
          View Services
        </Link>
      </div>
    </div>
  );
}
